import { useMemo } from 'react'
import { AlertTriangle, Save, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/Button'
import type { DirtyRowMap, EditableField } from '../hooks/useDirtyRows'

interface GerencialSaveConfirmProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dirty: DirtyRowMap
  pendingDeletes: Set<number>
  onConfirm: () => void
}

const SENSITIVE_FIELDS: EditableField[] = ['Data', 'Valor']

const FIELD_LABEL: Record<EditableField, string> = {
  Data: 'Data',
  Valor: 'Valor',
  CentroResultado: 'Centro de Resultado',
  NumeroNota: 'Número da Nota',
  Natureza: 'Natureza',
  CentroCusto: 'Centro de Custo',
  Empresa: 'Empresa',
  Descricao: 'Descrição',
}

/**
 * Confirm dialog before triggering the batch save. Shows breakdown by field
 * + pending deletes + extra warning when Data/Valor were changed. Enables
 * informed clicks at the moment of highest blast radius.
 */
export function GerencialSaveConfirm({
  open,
  onOpenChange,
  dirty,
  pendingDeletes,
  onConfirm,
}: GerencialSaveConfirmProps) {
  const breakdown = useMemo(() => {
    const counts = new Map<EditableField, number>()
    dirty.forEach((rowFields, rowId) => {
      // Skip rows scheduled for delete — their cell edits are moot.
      if (pendingDeletes.has(rowId)) return
      Object.keys(rowFields).forEach(k => {
        const field = k as EditableField
        counts.set(field, (counts.get(field) ?? 0) + 1)
      })
    })
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [dirty, pendingDeletes])

  const updateRowsCount = useMemo(() => {
    let n = 0
    dirty.forEach((_, rowId) => { if (!pendingDeletes.has(rowId)) n++ })
    return n
  }, [dirty, pendingDeletes])

  const deleteCount = pendingDeletes.size
  const sensitiveChanges = breakdown.filter(([f]) => SENSITIVE_FIELDS.includes(f))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-dark border-brand-dark-lighter sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-display">
            <Save className="inline-block w-5 h-5 mr-2 text-brand-primary" />
            Confirmar alterações
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm text-white">
            Você está prestes a aplicar as seguintes mudanças:
          </p>

          <ul className="space-y-1.5">
            {breakdown.map(([field, count]) => {
              const isSensitive = SENSITIVE_FIELDS.includes(field)
              return (
                <li
                  key={field}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                    isSensitive
                      ? 'bg-warning/10 border border-warning/30 text-warning font-medium'
                      : 'bg-brand-dark-lighter text-white'
                  }`}
                >
                  <span className="text-xs font-mono opacity-70">•</span>
                  <span>
                    {count} alteraç{count === 1 ? 'ão' : 'ões'} em{' '}
                    <strong>{FIELD_LABEL[field]}</strong>
                  </span>
                  {isSensitive && (
                    <AlertTriangle size={14} className="ml-auto text-warning" />
                  )}
                </li>
              )
            })}
            {deleteCount > 0 && (
              <li className="flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-error/10 border border-error/30 text-error font-medium">
                <Trash2 size={14} />
                <span>
                  {deleteCount} registro{deleteCount === 1 ? '' : 's'} a excluir
                </span>
              </li>
            )}
          </ul>

          {updateRowsCount === 0 && deleteCount > 0 && (
            <p className="text-xs text-brand-gray italic">
              Nenhuma edição de campo — apenas exclusões.
            </p>
          )}

          <div className="p-3 rounded-md bg-warning/10 border border-warning/30 flex items-start gap-2">
            <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
            <div className="text-sm text-white">
              <p className="font-medium text-warning">
                Esta ação reprocessa o cache da DRE
              </p>
              <p className="text-xs text-brand-gray mt-0.5">
                Pode levar 30–60 segundos. Não feche a aba.
              </p>
            </div>
          </div>

          {sensitiveChanges.length > 0 && (
            <p className="text-xs text-brand-gray">
              ⚠️ Alterações em <strong>Data</strong> ou <strong>Valor</strong> têm impacto
              maior no fechamento — confira antes de salvar.
            </p>
          )}

          {deleteCount > 0 && (
            <p className="text-xs text-error">
              ⚠️ As exclusões são <strong>definitivas</strong> e não podem ser desfeitas em massa.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            ← Revisar
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            <Save className="w-4 h-4 mr-1.5" />
            Salvar e reprocessar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
